import {
  CourierWalletSummary,
  CourierEarningItem,
  CourierWithdrawalItem,
  CourierWalletTransactionItem,
  CourierWalletDashboardData,
  CourierWalletAdminReport,
} from '../types';
import { getApiUrl } from './api';

export function getAdminToken(): string {
  if (typeof window === 'undefined') return '';
  return (localStorage.getItem('leton_admin_token') || '').trim();
}

export const MIN_COURIER_WITHDRAWAL_AMOUNT = 50000;

export const COURIER_DISPLAY_NAMES: Record<string, string> = {
  courier_sudirman: 'Kurir Chapter 5 - Sudirman',
  courier_kelakap: 'Kurir Chapter 6 - Kelakap 7',
  sudirman: 'Kasir Chapter 5 - Leton Sudirman',
  kelakap: 'Kasir Chapter 6 - Leton Kelakap 7',
  letgo: "Kasir Let'GO - MPP & Mobile",
  admin: 'Super Admin LetonKu',
};

export const OUTLET_DISPLAY_NAMES: Record<string, string> = {
  sudirman: 'Chapter 5 - Leton Sudirman',
  kelakap_7: 'Chapter 6 - Leton Kelakap 7',
  'letgo-mpp': "Let'GO - MPP & Mobile",
  ANY: 'Semua Outlet LetonKu (Bebas Pilih Kasir)',
};

export function resolveActorDisplayName(username?: string | null): string {
  if (!username) return 'Petugas LetonKu';
  const normalized = username.trim().toLowerCase();
  return COURIER_DISPLAY_NAMES[normalized] || username;
}

export function resolveOutletDisplayName(outletId?: string | null): string {
  if (!outletId) return 'Semua Outlet LetonKu';
  return OUTLET_DISPLAY_NAMES[outletId] || outletId;
}

export function mapDbWalletRow(row: any): CourierWalletSummary {
  const username = String(row?.courier_username || row?.courierUsername || '').trim();
  return {
    courierUsername: username,
    courierName: row?.courier_name || row?.courierName || resolveActorDisplayName(username),
    outletId: row?.outlet_id ?? row?.outletId ?? null,
    totalEarned: Number(row?.total_earned ?? row?.totalEarned ?? 0),
    availableBalance: Number(row?.available_balance ?? row?.availableBalance ?? 0),
    processingBalance: Number(row?.processing_balance ?? row?.processingBalance ?? 0),
    totalWithdrawn: Number(row?.total_withdrawn ?? row?.totalWithdrawn ?? 0),
    updatedAt: row?.updated_at || row?.updatedAt || new Date().toISOString(),
  };
}

export function mapDbEarningRow(row: any): CourierEarningItem {
  const username = String(row?.courier_username || row?.courierUsername || '').trim();
  return {
    id: String(row?.id || ''),
    orderId: String(row?.order_id || row?.orderId || ''),
    orderNumber: String(row?.order_number || row?.orderNumber || row?.order_id || ''),
    courierUsername: username,
    courierName: row?.courier_name || row?.courierName || resolveActorDisplayName(username),
    outletId: row?.outlet_id ?? row?.outletId ?? null,
    amount: Number(row?.amount ?? 0),
    status: (row?.status || 'CREDITED') as CourierEarningItem['status'],
    earnedAt: row?.earned_at || row?.earnedAt || new Date().toISOString(),
    reversedAt: row?.reversed_at ?? row?.reversedAt ?? null,
    reversalReason: row?.reversal_reason ?? row?.reversalReason ?? null,
  };
}

export function mapDbWithdrawalRow(row: any): CourierWithdrawalItem {
  const username = String(row?.courier_username || row?.courierUsername || '').trim();
  return {
    id: String(row?.id || ''),
    transactionCode: String(row?.transaction_code || row?.transactionCode || ''),
    courierUsername: username,
    courierName: row?.courier_name || row?.courierName || resolveActorDisplayName(username),
    courierOutletId: row?.courier_outlet_id ?? row?.courierOutletId ?? null,
    targetOutletId: row?.target_outlet_id ?? row?.targetOutletId ?? 'ANY',
    amount: Number(row?.amount ?? 0),
    paymentMethod: 'TUNAI',
    status: (row?.status || 'PENDING') as CourierWithdrawalItem['status'],
    requestedAt: row?.requested_at || row?.requestedAt || new Date().toISOString(),
    paidAt: row?.paid_at ?? row?.paidAt ?? null,
    paidByCashierUsername: row?.paid_by_cashier_username ?? row?.paidByCashierUsername ?? null,
    paidByCashierName: row?.paid_by_cashier_name ?? row?.paidByCashierName ?? null,
    paidAtOutletId: row?.paid_at_outlet_id ?? row?.paidAtOutletId ?? null,
    paidAtOutletName: row?.paid_at_outlet_name ?? row?.paidAtOutletName ?? null,
    cancelledAt: row?.cancelled_at ?? row?.cancelledAt ?? null,
    cancelReason: row?.cancel_reason ?? row?.cancelReason ?? null,
    notes: row?.notes ?? null,
  };
}

export function mapDbTransactionRow(row: any): CourierWalletTransactionItem {
  return {
    id: String(row?.id || ''),
    courierUsername: String(row?.courier_username || row?.courierUsername || ''),
    transactionType: row?.transaction_type || row?.transactionType || 'EARNING_CREDIT',
    amount: Number(row?.amount ?? 0),
    availableBefore: Number(row?.available_before ?? row?.availableBefore ?? 0),
    availableAfter: Number(row?.available_after ?? row?.availableAfter ?? 0),
    processingBefore: Number(row?.processing_before ?? row?.processingBefore ?? 0),
    processingAfter: Number(row?.processing_after ?? row?.processingAfter ?? 0),
    referenceOrderId: row?.reference_order_id ?? row?.referenceOrderId ?? null,
    referenceWithdrawalId: row?.reference_withdrawal_id ?? row?.referenceWithdrawalId ?? null,
    referenceCode: row?.reference_code ?? row?.referenceCode ?? null,
    actorUsername: row?.actor_username ?? row?.actorUsername ?? null,
    actorRole: row?.actor_role ?? row?.actorRole ?? null,
    outletId: row?.outlet_id ?? row?.outletId ?? null,
    description: row?.description ?? null,
    createdAt: row?.created_at || row?.createdAt || new Date().toISOString(),
  };
}

function buildAuthHeaders(): Record<string, string> {
  const token = getAdminToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['x-admin-token'] = token;
  }
  return headers;
}

/**
 * Fetch Courier Wallet Dashboard Data for logged-in Courier
 */
export async function fetchCourierWalletDashboard(): Promise<CourierWalletDashboardData> {
  const response = await fetch(getApiUrl('/api/courier/wallet'), {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Gagal memuat data dompet kurir.');
  }
  return {
    wallet: mapDbWalletRow(payload.wallet),
    earnings: Array.isArray(payload.earnings) ? payload.earnings.map(mapDbEarningRow) : [],
    withdrawals: Array.isArray(payload.withdrawals) ? payload.withdrawals.map(mapDbWithdrawalRow) : [],
    transactions: Array.isArray(payload.transactions) ? payload.transactions.map(mapDbTransactionRow) : [],
  };
}

/**
 * Submit a Courier Withdrawal Request (Minimum Rp50.000, Cash via Cashier)
 */
export async function requestCourierWithdrawal(params: {
  amount: number;
  targetOutletId?: string;
  notes?: string;
}): Promise<{ withdrawal: CourierWithdrawalItem; wallet: CourierWalletSummary }> {
  const cleanAmount = Math.floor(Number(params.amount || 0));
  if (!Number.isFinite(cleanAmount) || cleanAmount < MIN_COURIER_WITHDRAWAL_AMOUNT) {
    throw new Error('Minimal pencairan saldo adalah Rp50.000.');
  }

  const response = await fetch(getApiUrl('/api/courier/wallet/withdraw'), {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({
      amount: cleanAmount,
      targetOutletId: params.targetOutletId || 'ANY',
      notes: params.notes || '',
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Gagal mengajukan pencairan saldo.');
  }

  return {
    withdrawal: mapDbWithdrawalRow(payload.withdrawal),
    wallet: mapDbWalletRow(payload.wallet),
  };
}

/**
 * Cancel a Pending Courier Withdrawal Request & Restore Held Balance
 */
export async function cancelCourierWithdrawal(
  withdrawalIdOrCode: string,
  reason?: string
): Promise<{ withdrawal: CourierWithdrawalItem; wallet: CourierWalletSummary }> {
  const response = await fetch(
    getApiUrl(`/api/courier/wallet/withdraw/${encodeURIComponent(withdrawalIdOrCode)}/cancel`),
    {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: JSON.stringify({ reason: reason || 'Dibatalkan oleh kurir' }),
    }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Gagal membatalkan pengajuan pencairan.');
  }

  return {
    withdrawal: mapDbWithdrawalRow(payload.withdrawal),
    wallet: mapDbWalletRow(payload.wallet),
  };
}

/**
 * Fetch Courier Withdrawals for Cashier (Outlet Admin) or Super Admin
 */
export async function fetchCashierCourierWithdrawals(filters?: {
  search?: string;
  status?: string;
}): Promise<CourierWithdrawalItem[]> {
  const query = new URLSearchParams();
  if (filters?.search?.trim()) query.set('search', filters.search.trim());
  if (filters?.status?.trim()) query.set('status', filters.status.trim());

  const qs = query.toString();
  const response = await fetch(getApiUrl(`/api/cashier/courier-withdrawals${qs ? `?${qs}` : ''}`), {
    method: 'GET',
    headers: buildAuthHeaders(),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Gagal memuat daftar pencairan kurir.');
  }

  return Array.isArray(payload.withdrawals) ? payload.withdrawals.map(mapDbWithdrawalRow) : [];
}

/**
 * Confirm Cashier Cash Payout for a Courier Withdrawal -> status PAID
 */
export async function confirmCashierCourierWithdrawal(
  withdrawalIdOrCode: string,
  outletId?: string
): Promise<{ withdrawal: CourierWithdrawalItem; wallet: CourierWalletSummary }> {
  const response = await fetch(
    getApiUrl(`/api/cashier/courier-withdrawals/${encodeURIComponent(withdrawalIdOrCode)}/confirm`),
    {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: JSON.stringify({ outletId }),
    }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Gagal mengonfirmasi pembayaran pencairan kurir.');
  }

  return {
    withdrawal: mapDbWithdrawalRow(payload.withdrawal),
    wallet: mapDbWalletRow(payload.wallet),
  };
}

/**
 * Fetch Super Admin Courier Wallet Report
 */
export async function fetchAdminCourierWalletReport(): Promise<CourierWalletAdminReport> {
  const response = await fetch(getApiUrl('/api/admin/courier-wallet/report'), {
    method: 'GET',
    headers: buildAuthHeaders(),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || 'Gagal memuat laporan dompet kurir.');
  }

  const wallets = Array.isArray(payload.wallets) ? payload.wallets.map(mapDbWalletRow) : [];
  const withdrawals = Array.isArray(payload.withdrawals) ? payload.withdrawals.map(mapDbWithdrawalRow) : [];
  const earnings = Array.isArray(payload.earnings) ? payload.earnings.map(mapDbEarningRow) : [];
  const transactions = Array.isArray(payload.transactions) ? payload.transactions.map(mapDbTransactionRow) : [];

  return {
    totals: {
      totalEarned: Number(payload.totals?.totalEarned ?? wallets.reduce((s: number, w: CourierWalletSummary) => s + w.totalEarned, 0)),
      totalAvailableBalance: Number(
        payload.totals?.totalAvailableBalance ?? wallets.reduce((s: number, w: CourierWalletSummary) => s + w.availableBalance, 0)
      ),
      totalProcessingBalance: Number(
        payload.totals?.totalProcessingBalance ?? wallets.reduce((s: number, w: CourierWalletSummary) => s + w.processingBalance, 0)
      ),
      totalWithdrawn: Number(
        payload.totals?.totalWithdrawn ?? wallets.reduce((s: number, w: CourierWalletSummary) => s + w.totalWithdrawn, 0)
      ),
      pendingWithdrawalCount: Number(
        payload.totals?.pendingWithdrawalCount ?? withdrawals.filter((w: CourierWithdrawalItem) => w.status === 'PENDING').length
      ),
      paidWithdrawalCount: Number(
        payload.totals?.paidWithdrawalCount ?? withdrawals.filter((w: CourierWithdrawalItem) => w.status === 'PAID').length
      ),
    },
    wallets,
    withdrawals,
    earnings,
    transactions,
  };
}
